import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Query keys affected when student Scholar Dollar balances change
 * (manual transactions, payroll CSV import, loan payouts, approvals, etc.).
 *
 * Prefix matching covers parameterized keys (e.g. ['student'] → ['student', id]).
 * Exported so tests can assert completeness and callers can inspect the set.
 */
export const STUDENT_FINANCIAL_QUERY_KEYS: QueryKey[] = [
  ['students'],
  ['student'],
  ['active-students'],
  ['transactions'],
  ['student-transactions'],
  ['student-transaction-totals'],
  ['student-monthly-balance'],
  ['student-financial-summary'],
  ['student-paychecks'],
  ['pending-transactions'],
  ['student-transaction-summary'],
  ['platform-financial-summary'],
];

export type InvalidateStudentFinancialOptions = {
  /** Extra query keys to invalidate alongside the financial set (e.g. payroll-imports). */
  extraKeys?: QueryKey[];
};

/**
 * Invalidate student balance / transaction caches so Scholar Dollar numbers refetch.
 * Call after any operation that creates, approves, rejects, or deletes student transactions.
 */
export async function invalidateStudentFinancialQueries(
  queryClient: QueryClient,
  options?: InvalidateStudentFinancialOptions
): Promise<void> {
  const keys = [...STUDENT_FINANCIAL_QUERY_KEYS, ...(options?.extraKeys ?? [])];

  await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}
